import React, { PropsWithChildren, ReactElement, useEffect, useState } from 'react'
import { SwiperProps, SwiperSlideProps } from 'swiper/react/swiper-react'
import { Swiper, useSwiper } from 'swiper/react'
import { Square } from '../../utility/square/Square'
import { Swiper as SwiperClass } from 'swiper/types'
import { TriangleButton } from '../../buttons/triangle/TriangleButton'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'

type BpCardSlide = ReactElement<PropsWithChildren<SwiperSlideProps>>

export interface IBpCardCarouselProps extends SwiperProps {
  className?: string
  children?: BpCardSlide | BpCardSlide[]
  title?: string
  link?: {
    title: string
    path: string
  } | null
  topSection?: React.ReactNode
}

export const BpCardCarousel = (props: IBpCardCarouselProps) => {
  const { t } = useTranslation()
  const { className, children, title, link, ...swiperProps } = props
  const [activeIndex, setActiveIndex] = useState<number>()
  const [totalSlides, setTotalSlides] = useState<number>()

  const handleChange = (e: SwiperClass) => {
    setActiveIndex(e.realIndex + 1)
    setTotalSlides(e.slides.length || 1)
  }

  useEffect(() => {
    if (Array.isArray(children)) {
      setTotalSlides(children.length)
    }
  }, [children])

  const Title = () => {
    return (
      <h4 className='mb-[16px] truncate'>
        {title && `${title}:`} {t('other.countOfTotal', { count: activeIndex, total: totalSlides })}
      </h4>
    )
  }

  const Link = () => {
    return link ? (
      <NavLink className='text-default' to={link.path}>
        {link.title}
      </NavLink>
    ) : null
  }

  const Navigation = () => {
    const swiper = useSwiper()
    const isDisabledPrev = !swiper.allowSlidePrev || swiper.isBeginning
    const isDisabledNext = !swiper.allowSlideNext || swiper.isEnd
    return (
      <div className='mt-[1px] mr-[1px] table border-spacing-0 overflow-hidden rounded-[6px] shadow-outline shadow-focus'>
        <div className='flex'>
          <div
            className={`flex h-[24px] w-[24px] items-center justify-center border-r border-r-focus/[0.05] ${
              isDisabledPrev ? 'cursor-default bg-focus-100' : 'cursor-pointer hover:bg-grey-300'
            }`}
            onClick={() => !isDisabledPrev && swiper.slidePrev()}
          >
            <TriangleButton
              color={`${!isDisabledPrev ? '#A059FF' : 'rgba(102,132,167,0.5)'}`}
              position='left'
            />
          </div>
          <div
            className={`flex h-[24px] w-[24px] items-center justify-center ${
              isDisabledNext ? 'cursor-default bg-focus-100' : 'cursor-pointer hover:bg-grey-300'
            }`}
            onClick={() => !isDisabledNext && swiper.slideNext()}
          >
            <TriangleButton
              color={`${!isDisabledNext ? '#A059FF' : 'rgba(102,132,167,0.5)'}`}
              position='right'
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <Square className={`p-[16px] ${className}`}>
      <Swiper {...swiperProps} onActiveIndexChange={handleChange} onSwiper={handleChange}>
        <span slot='container-start'>
          <div className='flex items-start justify-between'>
            <Title />
            <div className={'flex gap-[12px]'}>
              {props.topSection}
              <div className='flex items-start gap-[14px]'>
                <Link />
                <Navigation />
              </div>
            </div>
          </div>
        </span>
        {children}
      </Swiper>
    </Square>
  )
}
